/**
 * Cliente HTTP axios com interceptores para JWT e tratamento de erros.
 *
 * O token é lido de sessionStorage a cada requisição (via interceptor),
 * garantindo que mudanças no contexto de auth se propagam automaticamente.
 *
 * Respostas 401 → limpam a sessão e redirecionam ao login.
 * Respostas 402 → erro de plano (PlanLimitError identificável).
 */
import axios from 'axios'
import type { AxiosError } from 'axios'
import { logger } from './logger'
import type {
  AppUser,
  Empresa,
  EscalaTrabalho,
  EspelhoDetail,
  EspelhoSummary,
  Feriado,
  Funcionario,
  FuncionarioAtestado,
  FuncionarioDependente,
  FuncionarioExame,
  FuncionarioHistoricoCargo,
  LgpdRequest,
  LgpdRequestType,
  LoginRequest,
  LoginResponse,
  PoliticaTrabalho,
  ProductMetrics,
  RepDevice,
  RepSyncResponse,
  RepTestResult,
  SubscriptionResponse,
  TenantDetail,
  TenantSummary,
  Tratamento,
  TratamentoTipo,
} from '../types/api'

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'

export const http = axios.create({ baseURL: BASE_URL, timeout: 15_000 })

// Injeta Authorization header com JWT de sessionStorage.
http.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('pontoflow_token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// Em 401 limpa sessão; em outros erros loga estruturadamente e propaga ao chamador.
// NUNCA loga body da request (pode conter senha, token ou PII).
http.interceptors.response.use(
  (res) => res,
  (error: AxiosError) => {
    const status = error.response?.status
    const method = error.config?.method?.toUpperCase()
    const url = error.config?.url

    if (status === 401) {
      sessionStorage.removeItem('pontoflow_token')
      // Evita redirecionar se já está na tela de login.
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login'
      }
    } else if (status && status >= 500) {
      logger.error('api_server_error', { status, method, url })
    } else if (status === 403) {
      logger.warn('api_forbidden', { method, url })
    } else if (status === 402) {
      logger.warn('api_plan_limit', { method, url })
    } else if (status && status >= 400 && status !== 404 && status !== 409) {
      // 404 e 409 são fluxos de negócio esperados, não erros de telemetria
      logger.warn('api_client_error', { status, method, url })
    }

    return Promise.reject(error)
  },
)

// ── Auth ──────────────────────────────────────────────────────────────────────

export const authApi = {
  login: (body: LoginRequest) =>
    http.post<LoginResponse>('/auth/login', body).then((r) => r.data),
}

// ── Users ─────────────────────────────────────────────────────────────────────

export const usersApi = {
  me: () => http.get<AppUser>('/users/me').then((r) => r.data),
  list: () => http.get<AppUser[]>('/users').then((r) => r.data),
}

// ── Billing ───────────────────────────────────────────────────────────────────

export const billingApi = {
  subscription: () =>
    http.get<SubscriptionResponse>('/billing/subscription').then((r) => r.data),
}

// ── Super-Admin ───────────────────────────────────────────────────────────────

export const adminApi = {
  listTenants: () =>
    http.get<TenantSummary[]>('/admin/tenants').then((r) => r.data),

  getTenant: (id: string, justificativa: string) =>
    http
      .get<TenantDetail>(`/admin/tenants/${id}`, { params: { justificativa } })
      .then((r) => r.data),

  changePlan: (id: string, plan: string, justificativa: string) =>
    http.patch(`/admin/tenants/${id}/plan`, { plan, justificativa }),

  suspend: (id: string, justificativa: string) =>
    http.post(`/admin/tenants/${id}/suspend`, { justificativa }),

  reactivate: (id: string, justificativa: string) =>
    http.post(`/admin/tenants/${id}/reactivate`, { justificativa }),

  metrics: () =>
    http.get<ProductMetrics>('/admin/metrics').then((r) => r.data),
}

// ── LGPD ─────────────────────────────────────────────────────────────────────

export const lgpdApi = {
  list: () =>
    http.get<LgpdRequest[]>('/lgpd/requests').then((r) => r.data),

  create: (tipo: LgpdRequestType, justificativa?: string) =>
    http
      .post<LgpdRequest>('/lgpd/requests', { tipo, justificativa })
      .then((r) => r.data),
}

// ── Feriados ──────────────────────────────────────────────────────────────────

export const feriadoApi = {
  list: (ano: number) =>
    http.get<Feriado[]>('/feriados', { params: { ano } }).then(r => r.data),

  create: (data: Omit<Feriado, 'id' | 'ativo'>) =>
    http.post<Feriado>('/feriados', data).then(r => r.data),

  update: (id: string, data: Partial<Feriado>) =>
    http.patch<Feriado>(`/feriados/${id}`, data).then(r => r.data),

  delete: (id: string) =>
    http.delete(`/feriados/${id}`),

  importarNacionais: (ano: number) =>
    http.post<{ inseridos: number; ignorados: number; ano: number }>(
      '/feriados/importar-nacionais', null, { params: { ano } }
    ).then(r => r.data),
}

// ── Políticas de Trabalho ─────────────────────────────────────────────────────

export const politicaApi = {
  list: () =>
    http.get<PoliticaTrabalho[]>('/politicas').then(r => r.data),

  create: (data: Omit<PoliticaTrabalho, 'id' | 'ativo' | 'created_at'>) =>
    http.post<PoliticaTrabalho>('/politicas', data).then(r => r.data),

  update: (id: string, data: Partial<PoliticaTrabalho>) =>
    http.patch<PoliticaTrabalho>(`/politicas/${id}`, data).then(r => r.data),

  delete: (id: string) =>
    http.delete(`/politicas/${id}`),

  clonar: (id: string) =>
    http.post<PoliticaTrabalho>(`/politicas/${id}/clonar`).then(r => r.data),
}

// ── Escalas de Trabalho ───────────────────────────────────────────────────────

export const escalaApi = {
  list: (apenas_ativas = false) =>
    http.get<EscalaTrabalho[]>('/escalas', { params: { apenas_ativas } }).then(r => r.data),

  create: (data: Omit<EscalaTrabalho, 'id' | 'ativo' | 'created_at'>) =>
    http.post<EscalaTrabalho>('/escalas', data).then(r => r.data),

  update: (id: string, data: Partial<EscalaTrabalho>) =>
    http.patch<EscalaTrabalho>(`/escalas/${id}`, data).then(r => r.data),

  delete: (id: string) =>
    http.delete(`/escalas/${id}`),
}

// ── Banco de Horas ────────────────────────────────────────────────────────────

export const bancoHorasApi = {
  listSaldos: () =>
    http.get('/banco-horas/saldos').then(r => r.data),

  saldo: (funcionario_id: string) =>
    http.get(`/banco-horas/saldo/${funcionario_id}`).then(r => r.data),

  extrato: (funcionario_id: string, data_inicio?: string, data_fim?: string) =>
    http.get(`/banco-horas/extrato/${funcionario_id}`, { params: { data_inicio, data_fim } }).then(r => r.data),

  lancar: (data: { funcionario_id: string; tipo: string; segundos: number; motivo: string; referencia_data: string; vencimento?: string; espelho_id?: string }) =>
    http.post('/banco-horas/lancar', data).then(r => r.data),

  ajuste: (data: { funcionario_id: string; segundos: number; motivo: string }) =>
    http.post('/banco-horas/ajuste', data).then(r => r.data),
}

// ── Empresas / Filiais ────────────────────────────────────────────────────────

export const empresaApi = {
  list: () =>
    http.get<Empresa[]>('/empresas').then((r) => r.data),

  get: (id: string) =>
    http.get<Empresa>(`/empresas/${id}`).then((r) => r.data),

  create: (data: Omit<Empresa, 'id' | 'ativo' | 'created_at'>) =>
    http.post<Empresa>('/empresas', data).then((r) => r.data),

  update: (id: string, data: Partial<Empresa>) =>
    http.patch<Empresa>(`/empresas/${id}`, data).then((r) => r.data),

  delete: (id: string) =>
    http.delete(`/empresas/${id}`),
}

// ── REP (Relógios de Ponto) ───────────────────────────────────────────────────

export const repApi = {
  list: () =>
    http.get<RepDevice[]>('/devices').then((r) => r.data),

  get: (id: string) =>
    http.get<RepDevice>(`/devices/${id}`).then((r) => r.data),

  create: (data: Partial<RepDevice>) =>
    http.post<RepDevice>('/devices', data).then((r) => r.data),

  update: (id: string, data: Partial<RepDevice>) =>
    http.patch<RepDevice>(`/devices/${id}`, data).then((r) => r.data),

  delete: (id: string) =>
    http.delete(`/devices/${id}`),

  testConnection: (id: string, username: string, password: string) =>
    http.post<RepTestResult>(`/devices/${id}/test`, { username, password }).then((r) => r.data),

  syncUsers: (id: string, username: string, password: string) =>
    http.post<RepSyncResponse>(`/devices/${id}/sync-users`, { username, password }).then((r) => r.data),

  listDeviceUsers: (id: string, username: string, password: string) =>
    http.post<Record<string, unknown>[]>(`/devices/${id}/users`, { username, password }).then((r) => r.data),
}

// ── Funcionários ──────────────────────────────────────────────────────────────

export const funcApi = {
  list: (apenas_ativos = true) =>
    http.get<Funcionario[]>('/funcionarios', { params: { apenas_ativos } }).then((r) => r.data),

  get: (id: string) =>
    http.get<Funcionario>(`/funcionarios/${id}`).then((r) => r.data),

  create: (data: Omit<Funcionario, 'id' | 'ativo'>) =>
    http.post<Funcionario>('/funcionarios', data).then((r) => r.data),

  update: (id: string, data: Partial<Funcionario>) =>
    http.patch<Funcionario>(`/funcionarios/${id}`, data).then((r) => r.data),

  deactivate: (id: string) =>
    http.post(`/funcionarios/${id}/desativar`),

  reativar: (id: string) =>
    http.post<Funcionario>(`/funcionarios/${id}/reativar`).then((r) => r.data),

  // ── Sub-recursos ─────────────────────────────────────────────────────────
  dependentes: {
    list: (funcId: string) =>
      http.get<FuncionarioDependente[]>(`/funcionarios/${funcId}/dependentes`).then(r => r.data),
    create: (funcId: string, data: Omit<FuncionarioDependente, 'id' | 'funcionario_id'>) =>
      http.post<FuncionarioDependente>(`/funcionarios/${funcId}/dependentes`, data).then(r => r.data),
    delete: (funcId: string, depId: string) =>
      http.delete(`/funcionarios/${funcId}/dependentes/${depId}`),
  },

  atestados: {
    list: (funcId: string) =>
      http.get<FuncionarioAtestado[]>(`/funcionarios/${funcId}/atestados`).then(r => r.data),
    create: (funcId: string, data: Omit<FuncionarioAtestado, 'id' | 'funcionario_id'>) =>
      http.post<FuncionarioAtestado>(`/funcionarios/${funcId}/atestados`, data).then(r => r.data),
    delete: (funcId: string, atesId: string) =>
      http.delete(`/funcionarios/${funcId}/atestados/${atesId}`),
  },

  exames: {
    list: (funcId: string) =>
      http.get<FuncionarioExame[]>(`/funcionarios/${funcId}/exames`).then(r => r.data),
    create: (funcId: string, data: Omit<FuncionarioExame, 'id' | 'funcionario_id' | 'vencido'>) =>
      http.post<FuncionarioExame>(`/funcionarios/${funcId}/exames`, data).then(r => r.data),
    delete: (funcId: string, exameId: string) =>
      http.delete(`/funcionarios/${funcId}/exames/${exameId}`),
  },

  historicoCargo: {
    list: (funcId: string) =>
      http.get<FuncionarioHistoricoCargo[]>(`/funcionarios/${funcId}/historico-cargo`).then(r => r.data),
    create: (funcId: string, data: Omit<FuncionarioHistoricoCargo, 'id' | 'funcionario_id'>) =>
      http.post<FuncionarioHistoricoCargo>(`/funcionarios/${funcId}/historico-cargo`, data).then(r => r.data),
    delete: (funcId: string, histId: string) =>
      http.delete(`/funcionarios/${funcId}/historico-cargo/${histId}`),
  },
}

// ── Espelhos de Ponto ────────────────────────────────────────────────────────

export const espApi = {
  list: (params?: { cpf_pis?: number; data_inicio?: string; data_fim?: string; status?: string }) =>
    http.get<EspelhoSummary[]>('/espelhos', { params }).then((r) => r.data),

  folhaMensal: (cpf_pis: number, ano: number, mes: number) =>
    http.get('/espelhos/folha-mensal', { params: { cpf_pis, ano, mes } }).then((r) => r.data),

  get: (id: string) =>
    http.get<EspelhoDetail>(`/espelhos/${id}`).then((r) => r.data),

  calcular: (cpf_pis: number, data: string) =>
    http.post<EspelhoSummary>('/espelhos/calcular', { cpf_pis, data }).then((r) => r.data),

  fechar: (id: string) =>
    http.post<EspelhoSummary>(`/espelhos/${id}/fechar`).then((r) => r.data),

  reabrir: (id: string, justificativa: string) =>
    http.post<EspelhoSummary>(`/espelhos/${id}/reabrir`, { justificativa }).then((r) => r.data),
}

// ── Tratamentos ───────────────────────────────────────────────────────────────

export const tratApi = {
  list: (espelhoId: string) =>
    http.get<Tratamento[]>(`/espelhos/${espelhoId}/tratamentos`).then((r) => r.data),

  create: (espelhoId: string, tipo: TratamentoTipo, motivo: string, detalhes?: Record<string, unknown>) =>
    http.post<Tratamento>(`/espelhos/${espelhoId}/tratamentos`, { tipo, motivo, detalhes }).then((r) => r.data),
}

// ── Utilitário de erro ────────────────────────────────────────────────────────

// ── Utilitário de erro ────────────────────────────────────────────────────────

export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: string })?.detail
    return detail ?? error.message
  }
  if (error instanceof Error) return error.message
  return 'Erro desconhecido'
}

export function isPlanLimitError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 402
}
